"use client";

import { useState, useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { usePoker } from "@/hooks/poker-context";
import type { GameVariant, GameMode, BlindScheduleDto, PublicRoom } from "@/lib/texas-holdem-types";
import { BlindScheduleConfig } from "@/components/poker/blind-schedule-config";
import { POKER_AVATARS, PokerAvatarIcon } from "@/components/poker/poker-avatars";
import { API_URL, API_KEY } from "@/lib/api-config";

const VARIANTS: { value: GameVariant; label: string; description: string; icon: string }[] = [
  { value: "NlHoldem", label: "No Limit Hold'em", description: "Classic poker", icon: "\u2660" },
  { value: "PlHoldem", label: "Pot Limit Hold'em", description: "Pot limit betting", icon: "\u2665" },
  { value: "PlOmaha", label: "Pot Limit Omaha", description: "4 hole cards", icon: "\u2666" },
  { value: "ShortDeck", label: "Short Deck (6+)", description: "Stripped deck, 36 cards", icon: "\u2663" },
];

const BLIND_PRESETS = [
  { sb: 5, bb: 10, minBuy: 500, maxBuy: 2000 },
  { sb: 10, bb: 20, minBuy: 1000, maxBuy: 5000 },
  { sb: 25, bb: 50, minBuy: 2500, maxBuy: 10000 },
  { sb: 50, bb: 100, minBuy: 5000, maxBuy: 20000 },
];

const NAME_KEY = "poker_player_name";
const LAST_GAME_KEY = "poker_last_game_code";
const AVATAR_KEY = "poker_avatar";

export default function PokerHomePage() {
  return (
    <Suspense>
      <PokerHomeInner />
    </Suspense>
  );
}

function PokerHomeInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { createRoom, joinRoom, reconnect, hasSavedSession, savedSessionRoomCode, error, setError } = usePoker();

  const requestedJoinCode = searchParams.get("join")?.toUpperCase() ?? "";
  const [mode, setMode] = useState<"home" | "create" | "join">(
    requestedJoinCode ? "join" : "home"
  );
  const [appliedJoinCode, setAppliedJoinCode] = useState(requestedJoinCode);
  const [name, setName] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(NAME_KEY) || "";
  });
  const [roomCode, setRoomCode] = useState(requestedJoinCode);
  const [lastGameCode] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(LAST_GAME_KEY);
  });
  const [avatar, setAvatar] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(AVATAR_KEY) || "";
  });
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);

  const [editingName, setEditingName] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gameMode, setGameMode] = useState<GameMode>("CashGame");
  const [variant, setVariant] = useState<GameVariant>("NlHoldem");
  const [selectedPreset, setSelectedPreset] = useState(1);
  const [customBlinds, setCustomBlinds] = useState(false);
  const [smallBlind, setSmallBlind] = useState(10);
  const [bigBlind, setBigBlind] = useState(20);
  const [minBuyIn, setMinBuyIn] = useState(1000);
  const [maxBuyIn, setMaxBuyIn] = useState(5000);
  const [turnTimer, setTurnTimer] = useState(30);
  const [autoDeal, setAutoDeal] = useState(true);
  const [startingStack, setStartingStack] = useState(5000);
  const [blindSchedule, setBlindSchedule] = useState<BlindScheduleDto | null>(null);
  const [maxPlayers, setMaxPlayers] = useState(9);
  const [isPublic, setIsPublic] = useState(false);
  const [publicRooms, setPublicRooms] = useState<PublicRoom[]>([]);

  if (requestedJoinCode !== appliedJoinCode) {
    setAppliedJoinCode(requestedJoinCode);
    if (requestedJoinCode) {
      setRoomCode(requestedJoinCode);
      setMode("join");
    }
  }

  useEffect(() => {
    const controller = new AbortController();

    fetch(`${API_URL}/api/poker/rooms/public`, {
      headers: { "X-API-Key": API_KEY },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) return;
        const rooms = await response.json() as PublicRoom[];
        setPublicRooms(rooms);
      })
      .catch(() => {});

    return () => controller.abort();
  }, []);

  const applyPreset = (index: number) => {
    setSelectedPreset(index);
    setCustomBlinds(false);
    const p = BLIND_PRESETS[index];
    setSmallBlind(p.sb);
    setBigBlind(p.bb);
    setMinBuyIn(p.minBuy);
    setMaxBuyIn(p.maxBuy);
  };

  const saveName = (n: string) => {
    setName(n);
    if (typeof window !== "undefined" && n.trim()) {
      localStorage.setItem(NAME_KEY, n.trim());
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const isTournament = gameMode === "Tournament";
      const result = await createRoom(name.trim(), {
        variant,
        gameMode,
        smallBlind,
        bigBlind,
        minBuyIn: isTournament ? startingStack : minBuyIn,
        maxBuyIn: isTournament ? startingStack : maxBuyIn,
        maxPlayers,
        turnTimerSeconds: turnTimer,
        autoDeal: isTournament ? true : autoDeal,
        isPublic,
        blindSchedule: blindSchedule ?? undefined,
      });
      if (result) {
        localStorage.setItem(LAST_GAME_KEY, result.code);
        router.push(`/poker/room/${result.code}`);
      }
    } catch {
      setError("Failed to create room");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!name.trim() || !roomCode.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await joinRoom(roomCode.trim(), name.trim());
      if (result) {
        localStorage.setItem(LAST_GAME_KEY, result.code);
        router.push(`/poker/room/${result.code}`);
      }
    } catch {
      setError("Failed to join room");
    } finally {
      setLoading(false);
    }
  };

  const initial = name.trim().charAt(0).toUpperCase() || "P";

  const saveAvatar = (a: string) => {
    setAvatar(a);
    localStorage.setItem(AVATAR_KEY, a);
    setShowAvatarPicker(false);
  };

  if (mode === "home") {
    return (
      <div className="flex min-h-[100dvh] flex-col poker-bg-gradient poker-suit-pattern p-4 pt-safe">
        <div className="w-full max-w-sm mx-auto space-y-4 pt-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAvatarPicker(!showAvatarPicker)}
              className="w-11 h-11 rounded-full flex items-center justify-center text-lg font-bold shadow-lg shrink-0 transition-all active:scale-[0.9] overflow-hidden"
              style={{ background: avatar ? "transparent" : "linear-gradient(135deg, var(--poker-cta), #d4a843)", color: "#fff" }}
            >
              {avatar ? <PokerAvatarIcon avatarId={avatar} size={44} /> : initial}
            </button>
            <div className="flex-1 min-w-0">
              {name.trim() && !editingName ? (
                <button onClick={() => setEditingName(true)} className="text-left">
                  <p className="text-[11px]" style={{ color: "var(--poker-gold-bright)" }}>Welcome back</p>
                  <div className="flex items-center gap-1.5">
                    <p className="text-white text-sm font-bold truncate">{name.trim()}</p>
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--poker-text-muted)" strokeWidth="2" strokeLinecap="round" className="w-3 h-3 shrink-0">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </div>
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    value={name}
                    onChange={(e) => saveName(e.target.value)}
                    placeholder="Your name..."
                    maxLength={20}
                    autoFocus
                    className="h-9 flex-1 min-w-0 text-sm px-3 poker-input"
                    onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) setEditingName(false); }}
                    onBlur={() => { if (name.trim()) setEditingName(false); }}
                  />
                  {name.trim() && (
                    <button
                      onClick={() => setEditingName(false)}
                      className="poker-icon-circle shrink-0"
                      style={{ width: "32px", height: "32px", background: "var(--poker-win)", border: "2px solid #2e7d32", boxShadow: "0 2px 0 #1b5e20" }}
                    >
                      <span className="text-xs text-white">&#10003;</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {showAvatarPicker && (
            <div className="poker-panel p-3 space-y-2">
              <p className="poker-label">Pick Your Look</p>
              <div className="grid grid-cols-5 gap-1.5">
                {POKER_AVATARS.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => saveAvatar(a.id)}
                    className="w-full aspect-square rounded-lg flex items-center justify-center transition-all active:scale-[0.85] overflow-hidden"
                    style={{
                      background: avatar === a.id ? "var(--poker-surface-warm)" : "var(--poker-room)",
                      border: avatar === a.id ? "2px solid var(--poker-cta)" : "2px solid transparent",
                    }}
                    title={a.name}
                  >
                    <PokerAvatarIcon avatarId={a.id} size={36} />
                  </button>
                ))}
              </div>
              {avatar && (
                <button
                  onClick={() => saveAvatar("")}
                  className="text-[10px] font-medium transition-colors"
                  style={{ color: "var(--poker-text-dim)" }}
                >
                  Clear
                </button>
              )}
            </div>
          )}

          <div className="poker-panel p-3 space-y-2.5">
            <div className="flex items-center gap-2">
              <input
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                placeholder="TABLE CODE"
                maxLength={6}
                className="flex-1 h-11 text-sm tracking-[0.2em] font-mono px-3 poker-input"
                onKeyDown={(e) => { if (e.key === "Enter" && roomCode.length >= 4 && name.trim()) handleJoin(); }}
              />
              <button
                onClick={handleJoin}
                disabled={loading || !name.trim() || roomCode.length < 4}
                className={`h-11 px-6 text-sm poker-btn-game poker-btn-game--sm ${roomCode.length >= 4 ? "poker-btn-game--orange" : "poker-btn-game--dark"}`}
              >
                {loading ? "..." : "Join"}
              </button>
            </div>
            {lastGameCode && (
              <button
                className="w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all active:scale-[0.98]"
                style={{ background: "var(--poker-void)", border: "2px solid var(--poker-border-purple)" }}
                onClick={() => { setRoomCode(lastGameCode); setMode("join"); }}
              >
                <span className="text-[10px] font-bold" style={{ color: "var(--poker-text-dim)" }}>Rejoin last table</span>
                <span className="text-sm font-mono font-bold">
                  <span style={{ color: "var(--poker-cta)" }}>{lastGameCode.slice(0, 3)}</span>
                  <span className="text-white">{lastGameCode.slice(3)}</span>
                </span>
              </button>
            )}
          </div>

          <div className="poker-divider-game"><span>Host a Table</span></div>

          <div className="grid grid-cols-2 gap-3">
            {VARIANTS.map((v) => (
              <button
                key={v.value}
                onClick={() => {
                  if (!name.trim()) return;
                  setVariant(v.value);
                  setMode("create");
                }}
                disabled={!name.trim()}
                className="poker-btn-game poker-btn-game--dark p-4 text-left"
              >
                <div className="text-2xl mb-2" style={{ color: "var(--poker-cta)" }}>{v.icon}</div>
                <p className="text-white text-sm font-bold leading-tight">{v.label}</p>
                <p className="text-[10px] mt-0.5" style={{ color: "var(--poker-text-dim)" }}>{v.description}</p>
              </button>
            ))}
          </div>

          <div className="poker-divider-game"><span>Open Tables</span></div>
          {publicRooms.length > 0 ? (
            <div className="space-y-2">
              {publicRooms.map((room) => {
                const isFull = room.emptySeats === 0;
                return (
                  <button
                    key={room.code}
                    onClick={() => {
                      if (!name.trim() || isFull) return;
                      setRoomCode(room.code);
                      setMode("join");
                    }}
                    disabled={!name.trim() || isFull}
                    className="w-full poker-panel p-3 flex items-center gap-3 transition-all active:scale-[0.98] disabled:opacity-40"
                  >
                    <div className="flex flex-col items-center gap-1 shrink-0">
                      <div className="poker-icon-circle poker-icon-circle--orange text-[10px] font-bold" style={{ width: 38, height: 38 }}>
                        {room.smallBlind}/{room.bigBlind}
                      </div>
                      <span className="poker-badge poker-badge--purple" style={{ fontSize: "8px", padding: "1px 5px" }}>
                        {room.variant === "NlHoldem" ? "NLHE" : room.variant === "PlOmaha" ? "PLO" : room.variant === "ShortDeck" ? "6+" : "PLH"}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0 text-left space-y-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-white font-mono">{room.code}</span>
                        {isFull && (
                          <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ background: "var(--poker-danger-bg)", color: "var(--poker-fold)", border: "1px solid #991b1b" }}>
                            Full
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: room.maxPlayers }, (_, i) => (
                          <span
                            key={i}
                            className="rounded-full"
                            style={{
                              width: 8,
                              height: 8,
                              background: i < room.playerCount ? "var(--poker-chrome)" : "transparent",
                              border: i < room.playerCount ? "1.5px solid var(--poker-chrome-border)" : "1.5px dashed var(--poker-text-muted)",
                              boxShadow: i < room.playerCount ? "0 1px 0 var(--poker-chrome-shadow)" : "none",
                            }}
                          />
                        ))}
                        <span className="text-[10px] ml-1 font-bold tabular-nums" style={{ color: isFull ? "var(--poker-fold)" : "var(--poker-text-dim)" }}>
                          {room.playerCount}/{room.maxPlayers}
                        </span>
                      </div>
                    </div>
                    <svg viewBox="0 0 24 24" fill="none" stroke={isFull ? "var(--poker-text-muted)" : "var(--poker-chrome)"} strokeWidth="2.5" strokeLinecap="round" className="w-5 h-5 shrink-0">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="poker-panel p-5 flex flex-col items-center gap-2">
              <p className="text-sm font-semibold" style={{ color: "var(--poker-text-dim)" }}>No active tables</p>
              <p className="text-[11px]" style={{ color: "var(--poker-text-dim)" }}>
                Create one above and toggle <span style={{ color: "var(--poker-text-secondary)" }}>Open Table</span> to list it here
              </p>
            </div>
          )}

          {hasSavedSession && savedSessionRoomCode && (
            <button
              className="w-full h-11 text-sm poker-btn-game poker-btn-game--sm poker-btn-game--orange"
              disabled={loading}
              onClick={async () => {
                setLoading(true);
                try {
                  const result = await reconnect();
                  if (result === "table") {
                    router.push(`/poker/table/${savedSessionRoomCode}`);
                  } else if (result === "lobby") {
                    router.push(`/poker/room/${savedSessionRoomCode}`);
                  }
                } catch {
                  setError("Failed to reconnect");
                } finally {
                  setLoading(false);
                }
              }}
            >
              {loading ? "Rejoining..." : `Rejoin ${savedSessionRoomCode}`}
            </button>
          )}

          {error && <p className="text-red-400 text-sm text-center">{error}</p>}

          <div className="flex justify-center gap-3 pt-2 pb-8">
            <span className="text-[9px] px-3 py-1 rounded-full font-medium" style={{ background: "var(--poker-room)", color: "var(--poker-text-muted)" }}>
              Support
            </span>
            <span className="text-[9px] px-3 py-1 rounded-full font-medium" style={{ background: "var(--poker-room)", color: "var(--poker-text-muted)" }}>
              v1.0.0
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "join") {
    return (
      <div className="flex min-h-[100dvh] flex-col poker-bg-gradient poker-suit-pattern p-4 pt-safe">
        <div className="w-full max-w-sm mx-auto space-y-5 pt-4">
          <div className="flex items-center gap-3">
            <button onClick={() => { setMode("home"); setError(null); }} className="poker-btn-game poker-btn-game--sm poker-btn-game--dark flex items-center justify-center" style={{ width: "var(--poker-btn-size, 44px)", height: "var(--poker-btn-size, 44px)", padding: 0 }}>&#8592;</button>
            <h2 className="text-lg font-bold text-white">Join Table</h2>
          </div>

          <div className="poker-panel p-4 space-y-4">
            <div className="space-y-1.5">
              <label className="poker-label">Player Name</label>
              <input
                value={name}
                onChange={(e) => saveName(e.target.value)}
                placeholder="Pick a name..."
                maxLength={20}
                autoFocus={!!roomCode}
                className="w-full h-11 px-4 text-sm poker-input"
                onKeyDown={(e) => { if (e.key === "Enter" && roomCode.length >= 4 && name.trim()) handleJoin(); }}
              />
            </div>

            <div className="space-y-1.5">
              <label className="poker-label">Table Code</label>
              <input
                value={roomCode}
                onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                placeholder="XXXXXX"
                maxLength={6}
                autoFocus={!roomCode}
                className="w-full h-14 px-4 text-center text-2xl tracking-[0.4em] font-mono poker-input"
                onKeyDown={(e) => { if (e.key === "Enter" && roomCode.length >= 4 && name.trim()) handleJoin(); }}
              />
            </div>
          </div>

          {error && <p className="text-red-400 text-sm text-center">{error}</p>}

          <button
            className="w-full h-14 text-lg poker-btn-game poker-btn-game--orange"
            onClick={handleJoin}
            disabled={loading || !name.trim() || roomCode.length < 4}
          >
            {loading ? "Joining..." : "Join Game"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[100dvh] flex-col poker-bg-gradient poker-suit-pattern p-4 pb-8 pt-safe">
      <div className="w-full max-w-sm mx-auto space-y-4 pt-4">
        <div className="flex items-center gap-3">
          <button onClick={() => { setMode("home"); setError(null); }} className="poker-btn-game poker-btn-game--sm poker-btn-game--dark flex items-center justify-center" style={{ width: "var(--poker-btn-size, 44px)", height: "var(--poker-btn-size, 44px)", padding: 0 }}>&#8592;</button>
          <h2 className="text-lg font-bold text-white">Host a Table</h2>
        </div>

        <div className="poker-panel p-4 space-y-1.5">
          <label className="poker-label">Player Name</label>
          <input
            value={name}
            onChange={(e) => saveName(e.target.value)}
            placeholder="Pick a name..."
            maxLength={20}
            className="w-full h-11 px-4 text-sm poker-input"
          />
        </div>

        <div className="poker-panel p-4 space-y-2">
          <label className="poker-label">Play Style</label>
          <div className="grid grid-cols-2 gap-2">
            {([
              { value: "CashGame" as const, label: "Cash Game", desc: "Rebuy anytime" },
              { value: "Tournament" as const, label: "Tournament", desc: "One life, last player wins" },
            ]).map((m) => (
              <button
                key={m.value}
                onClick={() => setGameMode(m.value)}
                className={`h-14 text-left px-3 poker-btn-game poker-btn-game--sm ${gameMode === m.value ? "poker-btn-game--orange" : "poker-btn-game--dark"}`}
              >
                <div className="text-xs font-bold">{m.label}</div>
                <div className="text-[9px] mt-0.5" style={{ color: gameMode === m.value ? "var(--poker-text-warm)" : "var(--poker-text-dim)" }}>{m.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="poker-panel p-4 space-y-4">
          <div className="space-y-2">
            <label className="poker-label">Variant</label>
            <div className="grid grid-cols-2 gap-2">
              {VARIANTS.map((v) => (
                <button
                  key={v.value}
                  onClick={() => setVariant(v.value)}
                  className={`h-10 text-xs poker-btn-game poker-btn-game--sm ${variant === v.value ? "poker-btn-game--orange" : "poker-btn-game--dark"}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          <div className="h-px" style={{ background: "var(--poker-surface-raised)" }} />

          <div className="space-y-2">
            <label className="poker-label">Blinds</label>
            <div className="grid grid-cols-4 gap-2">
              {BLIND_PRESETS.map((p, i) => (
                <button
                  key={i}
                  onClick={() => applyPreset(i)}
                  className={`h-10 text-xs poker-btn-game poker-btn-game--sm ${selectedPreset === i && !customBlinds ? "poker-btn-game--orange" : "poker-btn-game--dark"}`}
                >
                  {p.sb}/{p.bb}
                </button>
              ))}
            </div>
            <button
              onClick={() => setCustomBlinds(!customBlinds)}
              className="text-[10px] font-semibold transition-colors"
              style={{ color: "var(--poker-text-dim)" }}
            >
              {customBlinds ? "Use presets" : "Custom values"}
            </button>
          </div>

          {customBlinds && (
            <>
              <div className="h-px" style={{ background: "var(--poker-surface-raised)" }} />
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="poker-label" style={{ fontSize: "9px" }}>
                      Small Blind
                    </label>
                    <input
                      type="number"
                      value={smallBlind}
                      onChange={(e) => {
                        const v = parseInt(e.target.value) || 0;
                        setSmallBlind(v);
                        setBigBlind(v * 2);
                      }}
                      className="w-full h-9 px-3 text-sm poker-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="poker-label" style={{ fontSize: "9px" }}>
                      Big Blind
                    </label>
                    <input
                      type="number"
                      value={bigBlind}
                      onChange={(e) => setBigBlind(parseInt(e.target.value) || 0)}
                      className="w-full h-9 px-3 text-sm poker-input"
                    />
                  </div>
                </div>
                {gameMode === "CashGame" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="poker-label" style={{ fontSize: "9px" }}>
                        Min Buy-In
                      </label>
                      <input
                        type="number"
                        value={minBuyIn}
                        onChange={(e) => setMinBuyIn(parseInt(e.target.value) || 0)}
                        className="w-full h-9 px-3 text-sm poker-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="poker-label" style={{ fontSize: "9px" }}>
                        Max Buy-In
                      </label>
                      <input
                        type="number"
                        value={maxBuyIn}
                        onChange={(e) => setMaxBuyIn(parseInt(e.target.value) || 0)}
                        className="w-full h-9 px-3 text-sm poker-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {gameMode === "Tournament" && (
            <>
              <div className="h-px" style={{ background: "var(--poker-surface-raised)" }} />
              <div className="space-y-2">
                <label className="poker-label">Starting Stack</label>
                <div className="grid grid-cols-4 gap-2">
                  {[3000, 5000, 10000, 20000].map((s) => (
                    <button
                      key={s}
                      onClick={() => setStartingStack(s)}
                      className={`h-10 text-xs poker-btn-game poker-btn-game--sm ${startingStack === s ? "poker-btn-game--orange" : "poker-btn-game--dark"}`}
                    >
                      {s.toLocaleString()}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="poker-panel p-4 space-y-3">
          <div className="space-y-2">
            <label className="poker-label">Turn Timer</label>
            <div className="flex items-center gap-2">
              {[15, 30, 45, 60].map((t) => (
                <button
                  key={t}
                  onClick={() => setTurnTimer(t)}
                  className={`flex-1 h-9 text-xs poker-btn-game poker-btn-game--sm ${turnTimer === t ? "poker-btn-game--orange" : "poker-btn-game--dark"}`}
                >
                  {t}s
                </button>
              ))}
            </div>
          </div>

          <div className="h-px" style={{ background: "var(--poker-surface-raised)" }} />

          <div className="space-y-2">
            <label className="poker-label">Table Size</label>
            <div className="flex items-center gap-2">
              {([
                { value: 2, label: "2", desc: "Heads-Up" },
                { value: 6, label: "6", desc: "6-Max" },
                { value: 9, label: "9", desc: "Full Ring" },
              ] as const).map((t) => (
                <button
                  key={t.value}
                  onClick={() => setMaxPlayers(t.value)}
                  className={`flex-1 h-10 text-xs poker-btn-game poker-btn-game--sm ${maxPlayers === t.value ? "poker-btn-game--orange" : "poker-btn-game--dark"}`}
                >
                  <div className="font-bold">{t.label}</div>
                  <div className="text-[8px] mt-0.5" style={{ color: maxPlayers === t.value ? "var(--poker-text-warm)" : "var(--poker-text-dim)" }}>{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="h-px" style={{ background: "var(--poker-surface-raised)" }} />

          <button
            onClick={() => { if (gameMode !== "Tournament") setAutoDeal(!autoDeal); }}
            className="flex items-center justify-between w-full"
            style={{ color: gameMode === "Tournament" ? "var(--poker-text-dim)" : undefined }}
          >
            <span className="text-xs font-semibold" style={{ color: "var(--poker-text-secondary)" }}>
              Auto-deal next hand
              {gameMode === "Tournament" && <span className="text-[9px] ml-1" style={{ color: "var(--poker-text-dim)" }}>(always on)</span>}
            </span>
            <div className={`poker-toggle ${(autoDeal || gameMode === "Tournament") ? "poker-toggle--on" : "poker-toggle--off"}`}>
              <div className="poker-toggle-knob" />
            </div>
          </button>
        </div>

        <BlindScheduleConfig value={blindSchedule} onChange={setBlindSchedule} />

        <div className="poker-panel p-4">
          <button
            onClick={() => setIsPublic(!isPublic)}
            className="flex items-center justify-between w-full"
          >
            <div>
              <span className="text-xs font-semibold" style={{ color: "var(--poker-text-secondary)" }}>Open Table</span>
              <p className="text-[10px] mt-0.5" style={{ color: "var(--poker-text-dim)" }}>
                Anyone can find and join this table
              </p>
            </div>
            <div className={`poker-toggle ${isPublic ? "poker-toggle--on" : "poker-toggle--off"}`}>
              <div className="poker-toggle-knob" />
            </div>
          </button>
        </div>

        {error && <p className="text-red-400 text-sm text-center">{error}</p>}

        <button
          className="w-full h-14 text-lg poker-btn-game poker-btn-game--orange"
          onClick={handleCreate}
          disabled={loading || !name.trim()}
        >
          {loading ? "Creating..." : "Create Game"}
        </button>
      </div>
    </div>
  );
}
